import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  lisApi,
  type LisOrder,
  type PlaceLisOrderBody,
  type PlaceLisOrdersBody,
  type PlaceLisOrdersResult,
} from '@/api/lisApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * LIS (Epic Beaker) ordering for the case page.
 *
 * `selectedTenantId` is in every key, so switching tenant cannot leave another
 * tenant's orders on screen.
 */

/** The orderable tests, and whether this deployment can reach an LIS at all. */
export function useLisCatalog() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis', 'catalog', tenantId],
    enabled: !!tenantId,
    queryFn: () => lisApi.catalog(),
    // The catalog is a code constant on the backend; no point re-fetching it.
    staleTime: 60 * 60 * 1000,
  })
}

/**
 * The order number to show in the dialog before anything is placed.
 *
 * `staleTime: 0` and no retry: each opening gets its own number, and a stale
 * one from the cache would be shown for an order it will not be used for.
 */
export function useNextLisOrderId(enabled: boolean) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis', 'order-id', tenantId, enabled],
    enabled: enabled && !!tenantId,
    queryFn: () => lisApi.nextOrderId(),
    staleTime: 0,
    gcTime: 0,
  })
}

/** Laboratory results filed against one product, newest first. */
export function useLisResultsForProduct(productId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis', 'results', tenantId, productId],
    enabled: !!tenantId && !!productId,
    queryFn: () => lisApi.resultsForProduct(productId as string),
  })
}

/** Every laboratory result for one patient, newest first (z8nrz7g57g). */
export function usePatientLisResults(mrn: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis', 'results', 'patient', tenantId, mrn],
    enabled: !!tenantId && !!mrn,
    queryFn: () => lisApi.resultsForPatient(mrn as string),
    // A reader without integrations.read gets a 403; say so once, no retry.
    retry: false,
  })
}

export function useLisOrders(params: { patientId?: string; procedureId?: string; productId?: string } = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis', 'orders', tenantId, params.patientId ?? null, params.procedureId ?? null, params.productId ?? null],
    enabled: !!tenantId,
    queryFn: () => lisApi.listOrders(params),
  })
}

/** One submission, one order per selected test code. */
export function usePlaceLisOrders() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()

  return useMutation<PlaceLisOrdersResult, Error, PlaceLisOrdersBody>({
    mutationFn: (body) => lisApi.placeOrders(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lis', 'orders', tenantId] })
      // A placed order changes which stages are available next.
      void queryClient.invalidateQueries({ queryKey: ['lis-stages'] })
    },
  })
}

export function usePlaceLisOrder() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()

  return useMutation<LisOrder, Error, PlaceLisOrderBody>({
    mutationFn: (body) => lisApi.placeOrder(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lis', 'orders', tenantId] })
    },
  })
}

/**
 * Resend an order that was never acknowledged, under the SAME order number.
 * Offer this ONLY for `undelivered`: the backend 409s otherwise, and a rejected
 * order has to be corrected and placed as a new one.
 */
export function useRetryLisOrder() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()

  return useMutation<LisOrder, Error, string>({
    mutationFn: (orderId) => lisApi.retryOrder(orderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lis', 'orders', tenantId] })
    },
  })
}
